<template>
  <v-content class="pa-0">
    <v-card-title class="grey lighten-3">
      <v-row>
        <v-icon class="draggable">mdi-drag</v-icon>
        <v-col class="py-0">{{ extraLabel }}</v-col>
      </v-row>
    </v-card-title>
    <v-card-text class="pa-3">
      <v-container fluid grid-list-md class="pa-0">
        <v-layout row wrap>
          <v-flex sm4>
            <v-text-field
              label="Name"
              v-model="e.name"
              @input="$v.e.name.$touch()"
              @blur="$v.e.name.$touch()"
              :error-messages="nameErrors"
              :outlined="outlinedInputs"
            ></v-text-field>
          </v-flex>
          <v-flex sm4>
            <v-text-field
              label="Code"
              v-model="e.code"
              :outlined="outlinedInputs"
            ></v-text-field>
          </v-flex>
          <v-flex sm4>
            <v-text-field
              label="Capacity Change"
              v-model.number="e.capacityChange"
              type="number"
              min="0"
              :outlined="outlinedInputs"
            ></v-text-field>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex sm6>
            <taxes-list-select
              :taxCode="e.taxCode"
              @onSelectTaxType="onSelectTaxType($event)"
              :doResetSelectedTax="doResetSelectedTax"
              @onSelectedTaxReset="onSelectedTaxReset"
              :outlined="outlinedInputs"
            ></taxes-list-select>
          </v-flex>
          <v-flex sm6>
            <rate-type-list-select
              :rateTypeCode="e.rateTypeCode"
              @onSelect="onSelectRateType($event)"
              :doResetSelectedRateType="doResetSelectedRateType"
              @onSelectedRateTypeReset="onSelectedRateTypeReset"
              :outlined="outlinedInputs"
              hideNotInUse
            ></rate-type-list-select>
          </v-flex>
        </v-layout>
        <v-layout row wrap>
          <v-flex sm12>
            <v-select
              v-model="selectedFlags"
              :items="flags"
              item-text="label"
              item-value="propertyName"
              label="Flags"
              multiple
              chips
              deletable-chips
              :outlined="outlinedInputs"
            ></v-select>
          </v-flex>
        </v-layout>
      </v-container>
    </v-card-text>
    <div v-if="debug">{{ selectedFlags }}</div>
  </v-content>
</template>

<script>
import {
  EXTRA_SERVICE_TYPE_UPDATE_MUTATION,
  EXTRA_SERVICE_TYPE_CREATE_MUTATION,
} from "../../../../constants/graphql";
import TaxesListSelect from "../../shared/TaxesListSelect";
import RateTypeListSelect from "../../shared/RateTypeListSelect";
import { required } from "vuelidate/lib/validators";

export default {
  name: "ExtraEditor",

  components: {
    TaxesListSelect,
    RateTypeListSelect,
  },

  props: {
    extra: {
      type: Object,
      default: () => ({}),
    },

    doSaveExtra: {
      type: Boolean,
      default: false,
    },

    extraEditorDialog: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      e: { ...this.extra },
      debug: false,
      doResetSelectedTax: false,
      doResetSelectedRateType: false,
      selectedFlags: this.extra
        ? Object.keys(this.extra).filter(
            (key) =>
              typeof this.extra[key] == "boolean" && this.extra[key] == true
          )
        : [],
      flags: [
        { label: "Accommodation", propertyName: "isAccommodation" },
        { label: "Activity", propertyName: "isActivity" },
        { label: "Transport", propertyName: "isTransport" },
        { label: "Travel", propertyName: "isTravel" },
        { label: "Other", propertyName: "isOther" },
        { label: "Package", propertyName: "isPackage" },
        { label: "Child Only", propertyName: "isChildOnly" },
        { label: "Infant Only", propertyName: "isInfantOnly" },
        { label: "Commission", propertyName: "commission" },
        { label: "Markup", propertyName: "markup" },
        { label: "Discount", propertyName: "discount" },
        { label: "Mandatory", propertyName: "isMandatory" },
        { label: "No Report", propertyName: "noReport" },
        { label: "No Voucher", propertyName: "noVoucher" },
      ],
    };
  },

  methods: {
    saveExtra() {
      this.$emit("onResetSave");
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.setFlagsTypes();
        let mutation =
          this.context == "edit"
            ? EXTRA_SERVICE_TYPE_UPDATE_MUTATION
            : EXTRA_SERVICE_TYPE_CREATE_MUTATION;
        this.$apollo
          .mutate({
            mutation: mutation,
            variables: {
              ...this.e,
            },
          })
          .then(this.saveGqlObjectHandler, this.saveGqlObjectErrorHandler);
      }
    },
    saveGqlObjectHandler(result) {
      this.resetSelectedFields();
      this.$emit("saved", this.context);
    },
    saveGqlObjectErrorHandler(err) {
      console.log(err);
    },
    onSelectTaxType(data) {
      this.e.taxCode = data.code;
    },
    onSelectRateType(data) {
      this.e.rateTypeCode = data.code;
    },
    resetSelectedFields() {
      this.doResetSelectedTax = true;
      this.doResetSelectedRateType = true;
    },
    onSelectedTaxReset() {
      this.doResetSelectedTax = false;
    },
    onSelectedRateTypeReset() {
      this.doResetSelectedRateType = false;
    },
    setFlagsTypes() {
      this.flags.map((s) => {
        if (this.selectedFlags.indexOf(s.propertyName) > -1) {
          this.e[s.propertyName] = true;
        } else {
          this.e[s.propertyName] = false;
        }
      });
    },
  },

  computed: {
    outlinedInputs() {
      return this.$store.getters.localSettings("outlinedInputs");
    },
    extraLabel() {
      return this.extra.id != null ? this.extra.name : "New Extra Service Type";
    },
    context() {
      return this.extra.id != null ? "edit" : "create";
    },
    nameErrors() {
      let errors = [];
      if (!this.$v.e.name.$dirty) {
        return errors;
      }
      if (!this.$v.e.name.required) {
        errors.push("Name is required.");
      }
      return errors;
    },
  },

  watch: {
    extra(data) {
      let extra = { ...data };
      delete extra.__typename;
      this.e = extra;
      this.selectedFlags = Object.keys(extra).filter(
        (key) => typeof extra[key] == "boolean" && extra[key] == true
      );
    },
    extraEditorDialog(value) {
      if (!value) {
        this.e = {};
        this.selectedFlags = [];
        this.resetSelectedFields();
        this.$v.$reset();
      }
    },
    doSaveExtra(value) {
      if (value) {
        this.saveExtra();
      }
    },
  },
  validations: {
    e: {
      name: { required },
    },
  },
};
</script>

<style scoped></style>



// WEBPACK FOOTER //
// src/components/gt/admin/service-types/ExtraEditor.vue